import CopyToClipboard from 'react-copy-to-clipboard/lib';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import React, { useCallback, useState } from 'react';
import useLoading from '../../../../../../hooks/useLoading';
import { formatDateTimeOfUTC } from '../../../../../../utils/TimeUtils';
import BasicButton from '../../../../../components/_atom/button/BasicButton';
import { ButtonSize, ButtonTheme } from '../../../../../components/_atom/button/StyledButton';
import retryIcon from '../../../../../assets/images/icon_retry_grey.png';
import CreateWebhookModal, { SERVICE_ID } from './CreateWebhookModal';
import arrowRight from '../../../../../assets/images/arrow_right_btn_grey.png';
import KitContainer from '../../KitContainer';
import PageLoading from '../../../../../components/_atom/loading/PageLoading';
import EmptyDataTemplate from '../../../../dashboard/components/EmptyDataTemplate';
import BasicTable from '../../../../../components/_atom/table/BasicTable';
import { PagingBar } from '../../../../../components/_atom/paging/PagingBar';
import refreshIcon from '../../../../../assets/images/icon_refresh_grey.png';
import { ReactComponent as IconCopy } from '../../../../../assets/images/icon_copy.svg';
import TagBadge, { TagBadgeType } from '../../TagBadge';
import { logic } from '../../../../../../logic';
import { Webhook, WebhookEvent } from '../../../../../assets/constant/webhookManager.type';
import { KindOfKit, Kit } from '../../../../../assets/constant/kit.type';

const WEBHOOKS_API_DOCS_URL = 'https://docs.eqhub.eqbr.com/reference/overview-19';
const WEBHOOK_COLGROUP = [3, 3, 3, 1, 3];
const WEBHOOK_HEADERS = [
  { title: 'Webhook Name', accessor: 'name' },
  { title: 'ID', accessor: 'id' },
  { title: 'URL', accessor: 'url' },
  { title: 'Status', accessor: 'status' },
  { title: 'Event Type', accessor: 'type' },
];
const WEBHOOK_RENDERER = (data: Webhook) => {
  const statusText = data.is_active === 1 ? 'Active' : 'Deactivate';
  return {
    name: data.name,
    id: data.subscription_id,
    url: (
      <div style={{ display: 'flex', gap: 10, paddingRight: 10 }}>
        <div title={data.webhook_url} style={{ maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {data.webhook_url}
        </div>
        <CopyToClipboard text={data.webhook_url} onCopy={() => toast('Copied URL', { icon: '👏' })}>
          <IconCopy style={{ minWidth: 20, minHeight: 20, objectFit: 'contain', cursor: 'pointer' }} className="icon-copy" />
        </CopyToClipboard>
      </div>
    ),
    status: <TagBadge type={data.is_active === 1 ? TagBadgeType.BLUE : TagBadgeType.GREY} text={statusText} />,
    type: data.event_name,
  };
};
const WEBHOOKS_LIMIT = 10;

const WEBHOOK_HISTORY_COLGROUP = [3.3, 3.3, 3.3, 1.5, 3.5, 1.5];
const WEBHOOK_HISTORY_HEADERS = [
  { title: 'Webhook Name', accessor: 'webhookName' },
  { title: 'Event', accessor: 'event' },
  { title: 'Date(UTC)', accessor: 'date' },
  { title: 'Delivered', accessor: 'status' },
  { title: 'Response', accessor: 'result' },
  { title: 'Retry', accessor: 'retry' },
];
const WEBHOOK_HISTORY_LIMIT = 10;

const WebhookManager = (props: { kitDetail: Kit; kindOfKit: KindOfKit }) => {
  const { kitDetail, kindOfKit } = props;
  const kitId = kitDetail.kit_id || kitDetail.nft_kit_id;
  const navigate = useNavigate();
  const [kitWebhooks, setKitWebhooks] = useState<Webhook[]>([]);
  const [maxKitWebhooksNum, setMaxKitWebhooksNum] = useState(0);
  const getKitWebhooks = async (option?: { lastId?: number; offset?: number; order?: 'ASC' | 'DESC' }) => {
    const { result, error } = await logic.webhook.getWebhookList({
      limit: WEBHOOKS_LIMIT,
      resourceIdentifier: kitDetail.kit_id || kitDetail.nft_kit_id,
      ...option,
    });
    if (error) {
      return;
    }
    const { data: webhooks, total: maxWebhooksNum } = result;
    setKitWebhooks(webhooks);
    if (!option?.offset) {
      setMaxKitWebhooksNum(maxWebhooksNum);
    }
  };
  const webhookPageNum = Number(new URLSearchParams(window.location.search).get('webhook')) || 1;
  const maxWebhooksPageNum = maxKitWebhooksNum % WEBHOOKS_LIMIT === 0 ? maxKitWebhooksNum / WEBHOOKS_LIMIT : Math.floor(maxKitWebhooksNum / WEBHOOKS_LIMIT) + 1;
  const { loading: getWebhooksLoading } = useLoading({
    dependency: [webhookPageNum],
    synchronousFunction: async () => {
      await getKitWebhooks({ offset: (webhookPageNum - 1) * WEBHOOKS_LIMIT });
    },
  });

  const [txWebhooks, setTxWebhooks] = useState<Webhook[]>([]);
  const getTxWebhooks = async (option?: { lastId?: number; offset?: number; order?: 'ASC' | 'DESC' }) => {
    const { result, error } = await logic.webhook.getWebhookList({
      limit: WEBHOOKS_LIMIT,
      eventName: 'TRANSACTION',
      ...option,
    });
    if (error) {
      return;
    }
    const { data: webhooks } = result;
    setTxWebhooks(webhooks);
  };
  const { loading: getTxWebhooksLoading } = useLoading({
    dependency: [],
    synchronousFunction: async () => {
      await getTxWebhooks();
    },
  });

  const [isOpenRegisterWebhookModal, setIsOpenRegisterWebhookModal] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<number>();

  const [eventHistories, setEventHistories] = useState<WebhookEvent[]>([]);
  const [maxEventHistoriesNum, setMaxEventHistoriesNum] = useState(0);
  const getEventHistories = async (option?: { lastId?: number; offset?: number; order?: 'ASC' | 'DESC' }) => {
    if (!kitId) {
      return;
    }
    const { result, error } = await logic.webhook.getWebhookEventHistoryListALL({
      limit: WEBHOOKS_LIMIT,
      resourceIdentifier: [kitId],
      ...option,
    });
    if (error) {
      return;
    }
    const { data: eventHistories, total: maxEventHistoriesNum } = result;
    setEventHistories(eventHistories);
    if (!option?.offset) {
      setMaxEventHistoriesNum(maxEventHistoriesNum);
    }
  };
  const eventHistoryPageNum = Number(new URLSearchParams(window.location.search).get('event')) || 1;
  const maxEventHistoriesPageNum =
    maxEventHistoriesNum % WEBHOOK_HISTORY_LIMIT === 0
      ? maxEventHistoriesNum / WEBHOOK_HISTORY_LIMIT
      : Math.floor(maxEventHistoriesNum / WEBHOOK_HISTORY_LIMIT) + 1;
  const [isRefresh, setIsRefresh] = useState(false);
  const { loading: getEventHistoriesLoading } = useLoading({
    dependency: [eventHistoryPageNum, isRefresh],
    synchronousFunction: async () => {
      await getEventHistories({ offset: (eventHistoryPageNum - 1) * WEBHOOK_HISTORY_LIMIT });
    },
  });
  const retryWebhookEvent = async (webhook: any) => {
    const { result, error } = await logic.webhook.retryFailedSubscribeWebhook(webhook.subscription_id, webhook.webhook_id);
    if (error) {
      return;
    }
    setIsRefresh(!isRefresh);
    toast.success('Retry Success!');
  };
  const WEBHOOK_HISTORY_RENDERER = useCallback((data: WebhookEvent) => {
    const tagBadgeType = () => {
      if (data.status === 'SUCCEED') {
        return TagBadgeType.BLUE;
      } else if (data.status === 'FAILED') {
        return TagBadgeType.RED;
      } else if (data.status === 'CREATED') {
        return TagBadgeType.GREEN;
      }
      return TagBadgeType.GREY;
    };
    return {
      date: formatDateTimeOfUTC(data.created_at),
      webhookName: data.name ? data.name : 'Deleted Webhook',
      event: data.webhook_data?.event_name,
      status: <TagBadge type={tagBadgeType()} text={data.status} />,
      result: data.webhook_logs[-1]?.http_status_code,
      retry: (
        <>
          {data.name && data.status === 'FAILED' && (
            <BasicButton
              size={ButtonSize.DYNAMIC}
              theme={ButtonTheme.NORMAL}
              onClick={() => retryWebhookEvent(data)}
              style={{ width: 'fit-content', height: 'fit-content', padding: '5px 8px' }}
            >
              <img src={retryIcon} alt="retry" style={{ width: 14, height: 14, objectFit: 'contain', marginRight: 10 }} />
              Retry
            </BasicButton>
          )}
        </>
      ),
    };
  }, []);

  return (
    <div className="tab-content-wrapper">
      <CreateWebhookModal
        isOpen={isOpenRegisterWebhookModal}
        closeModal={() => setIsOpenRegisterWebhookModal(false)}
        refreshWebhook={() => getKitWebhooks({ offset: (webhookPageNum - 1) * WEBHOOKS_LIMIT })}
        defaultServiceId={selectedServiceId}
        defaultKitId={kitId}
      />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
        <BasicButton theme={ButtonTheme.NORMAL} onClick={() => window.open(WEBHOOKS_API_DOCS_URL, '_blank')}>
          User Guide & API Docs
          <img src={arrowRight} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
        </BasicButton>
        <BasicButton theme={ButtonTheme.NORMAL} onClick={() => navigate('/webhook')}>
          Webhook Manager Main
          <img src={arrowRight} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
        </BasicButton>
      </div>
      <KitContainer
        title={'Webhook List'}
        Button={
          <BasicButton
            size={ButtonSize.DYNAMIC}
            theme={ButtonTheme.PRIMARY}
            onClick={() => {
              setSelectedServiceId(kindOfKit === KindOfKit.TokenKit ? SERVICE_ID.TOKEN_KIT : SERVICE_ID.NFT_KIT);
              setIsOpenRegisterWebhookModal(true);
            }}
          >
            + New Webhook
          </BasicButton>
        }
      >
        {getWebhooksLoading ? (
          <PageLoading style={{ height: 477 }} />
        ) : (
          <>
            {kitWebhooks.length === 0 ? (
              <EmptyDataTemplate>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 500,
                    lineHeight: '21px',
                    color: '#7D828D',
                    whiteSpace: 'pre-line',
                  }}
                >
                  {'There is no created webhook about this kit.\nCreate a new webhook for kit and use Webhook Manager.'}
                </div>
              </EmptyDataTemplate>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
                <div className="kit-label" style={{ fontSize: 14 }}>
                  Kit Webhooks
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                  }}
                >
                  <BasicTable
                    data={kitWebhooks}
                    colGroup={WEBHOOK_COLGROUP}
                    headers={WEBHOOK_HEADERS}
                    renderer={WEBHOOK_RENDERER}
                    onClick={(webhook: Webhook) => navigate(`/webhook/${webhook.subscription_id}`)}
                  />
                  <PagingBar maxPageNum={maxWebhooksPageNum} query={'webhook'} />
                </div>
              </div>
            )}
          </>
        )}
        {!(getWebhooksLoading || getTxWebhooksLoading || (txWebhooks.length === 0 && kitWebhooks.length === 0)) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
            <div className="kit-label" style={{ fontSize: 14 }}>
              Transaction Webhook
            </div>
            <BasicTable
              data={txWebhooks}
              colGroup={WEBHOOK_COLGROUP}
              headers={WEBHOOK_HEADERS}
              renderer={WEBHOOK_RENDERER}
              onClick={(webhook: Webhook) => navigate(`/webhook/${webhook.subscription_id}`)}
            />
            {txWebhooks.length === 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '10px 0 20px',
                  gap: 20,
                }}
              >
                <div style={{ fontWeight: 600 }}>There is no transaction webhook created.</div>
                <BasicButton
                  size={ButtonSize.DYNAMIC}
                  theme={ButtonTheme.PRIMARY}
                  onClick={() => {
                    setSelectedServiceId(SERVICE_ID.TRANSACTION);
                    setIsOpenRegisterWebhookModal(true);
                  }}
                >
                  + New TX Webhook
                </BasicButton>
              </div>
            )}
          </div>
        )}
      </KitContainer>
      <KitContainer
        title={'Event History'}
        Button={
          <BasicButton size={ButtonSize.DYNAMIC} theme={ButtonTheme.NORMAL} onClick={() => setIsRefresh(!isRefresh)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <img src={refreshIcon} alt={'⟳'} width={14} /> Refresh
            </div>
          </BasicButton>
        }
      >
        {getEventHistoriesLoading ? (
          <PageLoading style={{ height: 477 }} />
        ) : (
          <>
            {eventHistories.length === 0 ? (
              <EmptyDataTemplate>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 500,
                    lineHeight: '21px',
                    color: '#7D828D',
                    whiteSpace: 'pre-line',
                  }}
                >
                  No event history yet
                </div>
              </EmptyDataTemplate>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                }}
              >
                <BasicTable data={eventHistories} colGroup={WEBHOOK_HISTORY_COLGROUP} headers={WEBHOOK_HISTORY_HEADERS} renderer={WEBHOOK_HISTORY_RENDERER} />
                <PagingBar maxPageNum={maxEventHistoriesPageNum} query={'event'} />
              </div>
            )}
          </>
        )}
      </KitContainer>
    </div>
  );
};

export default WebhookManager;
